import { Component, computed, inject, signal } from '@angular/core';
import { FirmContextService } from '../../services/firm-context.service';
import { UserService } from '../../services/user.service';

/**
 * MP-5653: says WHOSE taxonomy a settings screen is editing.
 *
 * Taxonomy is PARENT-owned (MP-5641): one definition list per family, read by
 * every child, written only by a parent-level FM (MP-5638). The four settings
 * screens are unchanged; this banner is what makes them the family's "parent
 * settings area" for a root FM, and tells a child-scoped user why the create,
 * edit and delete controls are missing.
 *
 * Renders nothing for a standalone firm - every Staftr and PWC firm - so those
 * screens are byte-identical. A child-attached user never has a loaded family
 * (the header clears the context for them), so the parent-context branch can
 * only ever show for a root-attached user whose family has children.
 */
@Component({
  selector: 'app-family-settings-banner',
  standalone: true,
  template: `
    @if (isParentContext()) {
      <div class="family-settings-banner mb-3" role="note">
        Parent firm settings for <strong>{{ rootFirmName() }}</strong>.
        The acting firm selected in the header does not apply here: changes on this page are shared by every firm in the family.
      </div>
    } @else if (isReadOnlyChild()) {
      <div class="family-settings-banner mb-3" role="note">
        These settings are defined by your parent firm and are read-only here.
      </div>
    }
  `,
  styles: `
    .family-settings-banner {
      padding: 0.75rem 1rem;
      border-radius: 6px;
      border-left: 4px solid var(--primary-color);
      background: var(--surface-100);
      color: var(--text-color);
      font-size: 0.9rem;
      line-height: 1.4;
    }
  `,
})
export class FamilySettingsBannerComponent {
  private readonly userService = inject(UserService);
  private readonly firmContext = inject(FirmContextService);

  /** The family root's name, once the header has loaded the family. */
  readonly rootFirmName = computed(() => this.firmContext.rootFirm()?.name || '');

  /** A root-attached user whose family has children: the parent settings area. */
  readonly isParentContext = computed(() => this.userService.isFamilyParentContext());

  /**
   * A child-scoped user: the server refuses their writes and MP-5641 hides the
   * controls, so say why. Fixed for the session, like the user's own firm.
   */
  readonly isReadOnlyChild = signal(
    !this.userService.isFamilyRootUser() && !this.userService.canManageTaxonomy()
  );
}
